// SIMPLE USER DASHBOARD - Shows exact Excel data from admin
import React, { useState, useEffect } from 'react';
import { simpleExcelStorage, SimpleExcelData } from '../services/simpleExcelStorage';
import { useAuth } from '../context/AuthContext';

interface SimpleUserDashboardProps {
  onClose?: () => void;
}

const SimpleUserDashboard: React.FC<SimpleUserDashboardProps> = ({ onClose }) => {
  const { user } = useAuth();
  const [userTasks, setUserTasks] = useState<SimpleExcelData[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const loadUserTasks = async () => {
    if (!user) return;

    setLoading(true);
    try {
      console.log('📥 SimpleUserDashboard: Loading Excel tasks for user:', user.uid);
      
      const tasks = await simpleExcelStorage.getUserExcelData(user.uid);
      
      console.log('📥 SimpleUserDashboard: Loaded tasks:', tasks.length);
      setUserTasks(tasks);
      setLastRefresh(new Date());
      
    } catch (error) {
      console.error('❌ SimpleUserDashboard: Error loading tasks:', error);
      alert(`Error loading tasks: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserTasks();
  }, [user]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('high') || statusLower.includes('urgent')) return '#ff4444';
    if (statusLower.includes('medium')) return '#ffaa00';
    if (statusLower.includes('low')) return '#44ff44';
    if (statusLower.includes('completed') || statusLower.includes('done')) return '#00aa00';
    return '#666666';
  };

  const getPriorityColor = (priority: string) => {
    const priorityLower = priority.toLowerCase();
    if (priorityLower.includes('high') || priorityLower.includes('urgent')) return '#ff4444';
    if (priorityLower.includes('medium')) return '#ffaa00';
    if (priorityLower.includes('low')) return '#44ff44';
    return '#666666';
  };

  if (!user) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>👤 User Dashboard</h2>
        <p>Please login to view your tasks</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>👤 User Dashboard - Excel Tasks</h2>
        {onClose && (
          <button 
            onClick={onClose} 
            style={{ padding: '10px 20px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '4px' }}
          >
            ❌ Close
          </button>
        )}
      </div>

      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button 
          onClick={loadUserTasks} 
          disabled={loading}
          style={{ 
            padding: '10px 20px', 
            backgroundColor: loading ? '#ccc' : '#2196F3', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? '🔄 Loading...' : '🔄 Refresh'}
        </button>
        
        <span style={{ fontSize: '14px', color: '#666' }}>
          Last updated: {formatDate(lastRefresh.toISOString())}
        </span>
        
        <span style={{ fontSize: '14px', color: '#666' }}>
          Found: {userTasks.length} task(s)
        </span>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '18px', color: '#666' }}>Loading your tasks...</div>
        </div>
      )}

      {!loading && userTasks.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
          <h3>📋 No Tasks Assigned</h3>
          <p>You don't have any Excel tasks assigned to you yet.</p>
          <p>Admin will create tasks and assign them to you.</p>
        </div>
      )}

      {!loading && userTasks.map((task, taskIndex) => (
        <div key={task.id} style={{ 
          border: '2px solid #e0e0e0', 
          margin: '20px 0', 
          padding: '20px', 
          borderRadius: '8px',
          backgroundColor: '#fafafa'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ margin: 0, color: '#333' }}>
              📋 Excel Task #{taskIndex + 1}
            </h3>
            <div style={{ fontSize: '12px', color: '#666' }}>
              Created: {formatDate(task.createdAt)}
              <br />
              Updated: {formatDate(task.updatedAt)}
            </div>
          </div>

          {/* Show exact data from admin */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              borderCollapse: 'collapse', 
              width: '100%', 
              backgroundColor: 'white',
              borderRadius: '4px',
              overflow: 'hidden',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f0f0f0' }}>
                  {task.headers.map((header, i) => (
                    <th key={i} style={{ 
                      border: '1px solid #ddd', 
                      padding: '12px 8px', 
                      backgroundColor: '#f0f0f0',
                      fontWeight: 'bold',
                      textAlign: 'left'
                    }}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {task.rows.map((row, rowIndex) => (
                  <tr key={rowIndex} style={{ 
                    backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f9f9f9'
                  }}>
                    {row.map((cell, colIndex) => {
                      const header = task.headers[colIndex]?.toLowerCase() || '';
                      let cellStyle: React.CSSProperties = {
                        border: '1px solid #ddd', 
                        padding: '10px 8px',
                        minWidth: '120px'
                      };

                      // Apply colors based on header type
                      if (header.includes('status')) {
                        cellStyle.backgroundColor = getStatusColor(cell);
                        cellStyle.color = 'white';
                        cellStyle.fontWeight = 'bold';
                      } else if (header.includes('priority')) {
                        cellStyle.backgroundColor = getPriorityColor(cell);
                        cellStyle.color = 'white';
                        cellStyle.fontWeight = 'bold';
                      } else if (header.includes('due date') || header.includes('date')) {
                        cellStyle.fontStyle = 'italic';
                        cellStyle.color = '#0066cc';
                      }

                      return (
                        <td key={colIndex} style={cellStyle}>
                          {cell || '-'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {task.rows.length === 0 && (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              color: '#666',
              fontStyle: 'italic'
            }}>
              No data rows in this task
            </div>
          )}
        </div>
      ))}

      {/* Info Panel */}
      <div style={{ 
        marginTop: '30px', 
        padding: '15px', 
        backgroundColor: '#e8f4fd', 
        borderRadius: '8px',
        border: '1px solid #b3d9ff'
      }}>
        <h3 style={{ margin: '0 0 10px 0', color: '#0066cc' }}>💡 How This Works</h3>
        <ul style={{ margin: 0, paddingLeft: '20px' }}>
          <li><strong>Simple Method:</strong> Admin creates Excel data → You see exact same data</li>
          <li><strong>Real-time:</strong> Click refresh to get latest data from admin</li>
          <li><strong>Direct Storage:</strong> Data is stored in AWS and loaded instantly</li>
          <li><strong>No Complexity:</strong> What admin types is exactly what you see</li>
        </ul>
      </div>
    </div>
  );
};

export default SimpleUserDashboard;
